import { useEffect, useRef, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTable, usePagination } from 'react-table';
import { useCreateTemplateAction } from '../../client/pass-templates-new/hooks/useCreateTemplateAction';
import {
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Spinner,
  Tooltip,
  Divider,
  VStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Icon,
  useToast,
} from '@chakra-ui/react';
import TableBox from '../../common/TableBox';
import ReactTablePagination from '../../../common/ReactTablePagination.tsx';
import PassesRenderStatus from './PassesRenderStatus';
import { CustomViewIcon } from '../../../theme/icons/CustomViewIcon';
import { CustomEditIcon } from '../../../theme/icons/CustomEditIcon';
import { CustomCopyLinkIcon } from '../../../theme/icons/CustomCopyLinkIcon';
import CustomToast from '../../../common/CustomToast';
import PassesListDownload from './PassesListDownload';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  selectNewFiltersSet,
  selectPasses,
  selectSortValues,
  mergePasses,
  setNavigatedFromPassPage,
  setNewFiltersSet,
  updateSortValues,
} from '../../../store/redux/slices/passList.slice';
import { MeContext } from '../../../context/MeContext';
import type { MeContextState } from '../../../store/types/account';
import PassesEmptyState from './PassesEmptyState';
import CustomPassIcon from '../../../theme/icons/CustomPassIcon';
import CustomNoResultsIcon from '../../../theme/icons/CustomNoResultsIcon';
import CustomTemplateIcon from '../../../theme/icons/CustomTemplateIcon';
import { useListPassTemplatesQuery } from '../../../store/redux/api/templateApi';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';

const defaultPropGetter = (..._args: unknown[]) => ({});

export default function PassesListTable({
  columns,
  data,
  fetchData,
  isLoading,
  total,
  pageCount: controlledPageCount,
  getColumnProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
  query,
}) {
  const isReadOnly = useIsReadOnly();
  const { createTemplateTo } = useCreateTemplateAction();

  const toast = useToast();
  const dispatch = useAppDispatch();
  const newFiltersSet = useAppSelector(selectNewFiltersSet);
  const sortValues = useAppSelector(selectSortValues);
  const passes = useAppSelector(selectPasses);

  const SORTABLE_COLUMNS: Record<string, string> = {
    externalId: 'extId',
    passTemplate: 'passTemplateName',
    addedToWallet: 'addedAt',
  };

  const handleColumnSort = (columnId: string) => {
    const sortKey = SORTABLE_COLUMNS[columnId];
    if (!sortKey) return;
    const isCurrentSort = sortValues.sortBy === sortKey;
    const nextOrder =
      isCurrentSort && sortValues.sortOrder === 'asc' ? 'desc' : 'asc';
    dispatch(updateSortValues({ sortBy: sortKey, sortOrder: nextOrder }));
  };

  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;
  const filterEntityRef = useRef(filterEntity);
  const entityId = filterEntity?.uuid;

  const {
    data: templatesData,
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
    isSuccess: isTemplatesSuccess,
    isError: isTemplatesError,
  } = useListPassTemplatesQuery({
    entityId,
    skipImages: true,
    fields: 'name',
  });

  const hasNoTemplates =
    isTemplatesSuccess &&
    !isTemplatesError &&
    (templatesData?.data?.length ?? 0) === 0;

  const currentPage = newFiltersSet ? 0 : (passes?.pageIndex ?? 0);
  const currentPageSize = passes?.pageSize ?? 10;

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: currentPage, pageSize: currentPageSize },
      manualPagination: true,
      pageCount: controlledPageCount,
    },
    usePagination
  );

  const setPageSize = (size: number) => {
    setTablePageSize(size);
    dispatch(mergePasses({ pageSize: size }));
  };

  useEffect(() => {
    gotoPage(currentPage);
  }, [gotoPage, currentPage]);

  useEffect(() => {
    if (newFiltersSet && pageIndex !== 0) {
      gotoPage(0);
      return;
    }
    if (newFiltersSet) {
      dispatch(setNewFiltersSet(false));
    }
    fetchData({ pageIndex, pageSize });
  }, [query, fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (filterEntity !== filterEntityRef.current) {
      filterEntityRef.current = filterEntity;
      gotoPage(0);
      fetchData({ pageSize: 10, pageIndex: 0 });
    }
  }, [filterEntity]);

  function preserveListState() {
    dispatch(setNewFiltersSet(false));
    dispatch(setNavigatedFromPassPage(true));
  }

  if (isLoading) {
    return (
      <Spinner
        width="52px"
        height="52px"
        thickness="4px"
        speed="0.65s"
        emptyColor="quinaryBackground"
        mt={4}
        mx="auto"
      />
    );
  }

  if (data.length === 0 && !isLoading && query === '') {
    if ((isTemplatesLoading || isTemplatesFetching) && !isTemplatesError) {
      return (
        <Spinner
          width="52px"
          height="52px"
          thickness="4px"
          speed="0.65s"
          emptyColor="quinaryBackground"
          mt={4}
          mx="auto"
        />
      );
    }

    if (hasNoTemplates) {
      return (
        <PassesEmptyState
          iconSvg={<CustomTemplateIcon boxSize="40px" />}
          heading="Create a template to get started"
          text="Passes are built from templates. Create your first template, then come back here to issue passes from it."
          children={
            isReadOnly ? (
              <LockedActionButton
                reason="create-templates"
                size="sm"
                width="100%"
              >
                Create a template
              </LockedActionButton>
            ) : (
              <Button
                aria-label="Create a template"
                size="sm"
                width="100%"
                as={RouterLink}
                to={createTemplateTo}
              >
                Create a template
              </Button>
            )
          }
        />
      );
    }

    return (
      <PassesEmptyState
        iconSvg={<CustomPassIcon boxSize="40px" />}
        heading="No passes created yet"
        text="Create a pass from one of your templates."
        children={
          isReadOnly ? (
            <LockedActionButton reason="issue-passes" size="sm" width="100%">
              Create your first pass
            </LockedActionButton>
          ) : (
            <Button
              aria-label="Create your first pass"
              size="sm"
              width="100%"
              as={RouterLink}
              to="/passes/create"
            >
              Create your first pass
            </Button>
          )
        }
      />
    );
  } else if (data.length === 0 && !isLoading && query !== '') {
    return (
      <PassesEmptyState
        iconSvg={<CustomNoResultsIcon boxSize="50px" />}
        heading="No passes found"
        text="Check the ID and try again or adjust your filters."
      />
    );
  }

  return (
    <>
      <TableBox>
        <Flex direction="row" gap="12px">
          <Text textStyle="heading3">Passes</Text>
          <Text
            textStyle="bodyFamilyRegular"
            fontSize="14px"
            color="#989898"
            position="relative"
            top="12px"
          >
            {total} passes found
          </Text>
        </Flex>
        <Divider borderColor="#E0E0E0" mt="15px" mb="15px" />
        <Table {...getTableProps()}>
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => {
                  return (
                    <Th
                      {...column.getHeaderProps()}
                      {...column.getToggleHiddenProps()}
                      isNumeric={column.isNumeric}
                      fontFamily="Inter Regular, sans-serif"
                      textTransform="none"
                      fontSize="15px"
                      color="#4C4C4C"
                      borderBottom="none"
                      pb="20px"
                      px="16px"
                      textAlign={column.id === 'actions' ? 'center' : undefined}
                      cursor={
                        SORTABLE_COLUMNS[column.id] ? 'pointer' : 'default'
                      }
                      onClick={() => handleColumnSort(column.id)}
                      _hover={
                        SORTABLE_COLUMNS[column.id]
                          ? { color: '#333' }
                          : undefined
                      }
                    >
                      <Flex
                        align="center"
                        gap="8px"
                        justify={column.id === 'actions' ? 'center' : undefined}
                      >
                        {column.render('Header')}
                        {SORTABLE_COLUMNS[column.id] && (
                          <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            gap="3px"
                          >
                            <Icon
                              viewBox="0 0 10 6"
                              w="10px"
                              h="6px"
                              color={
                                sortValues.sortBy ===
                                  SORTABLE_COLUMNS[column.id] &&
                                sortValues.sortOrder === 'asc'
                                  ? '#4C4C4C'
                                  : '#AEAEAE'
                              }
                            >
                              <path
                                d="M1 5L5 1L9 5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                            </Icon>
                            <Icon
                              viewBox="0 0 10 6"
                              w="10px"
                              h="6px"
                              color={
                                sortValues.sortBy ===
                                  SORTABLE_COLUMNS[column.id] &&
                                sortValues.sortOrder === 'desc'
                                  ? '#4C4C4C'
                                  : '#AEAEAE'
                              }
                            >
                              <path
                                d="M1 1L5 5L9 1"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                              />
                            </Icon>
                          </Flex>
                        )}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>

          {data.length > 0 && (
            <Tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    cursor="pointer"
                    _hover={{ bg: '#FAFAFA' }}
                  >
                    {row.cells.map((cell) => {
                      const isActions = cell.column.id === 'actions';
                      const cellContent =
                        cell.column.id === 'externalId' ? (
                          <VStack spacing={1} alignItems="flex-start">
                            <Tooltip
                              label={row.original.udi}
                              placement="right"
                              openDelay={300}
                              fontSize="12px"
                              borderRadius="5px"
                              textAlign="left"
                              p={1}
                              zIndex={1000}
                              isDisabled={
                                !row.original.udi ||
                                row.original.udi.length <= 18
                              }
                            >
                              <Text
                                textStyle="bodyFamilyMedium"
                                fontSize="14.5px"
                                color="#4C4C4C"
                              >
                                {row.original.udi
                                  ? `Ext ID: ${row.original.udi.length > 18 ? row.original.udi.slice(0, 18) + '...' : row.original.udi}`
                                  : '–'}
                              </Text>
                            </Tooltip>
                            <Tooltip
                              label={row.original.passUuid}
                              placement="right"
                              openDelay={300}
                              fontSize="12px"
                              borderRadius="5px"
                              textAlign="center"
                              p={1}
                              zIndex={1000}
                            >
                              <Text
                                textStyle="bodyFamilyRegular"
                                fontSize="13.5px"
                                color="#4C4C4C"
                              >
                                {row.original.passUuid ? (
                                  <>
                                    <Text
                                      as="span"
                                      textStyle="bodyFamilyMedium"
                                    >
                                      UUID:{' '}
                                    </Text>
                                    {row.original.passUuid.slice(0, 18)}...
                                  </>
                                ) : (
                                  '–'
                                )}{' '}
                              </Text>
                            </Tooltip>
                          </VStack>
                        ) : cell.column.id === 'passTemplate' ? (
                          <VStack spacing={0} alignItems="flex-start">
                            <Text
                              textStyle="bodyFamilyMedium"
                              fontSize="14.5px"
                              color="#4C4C4C"
                            >
                              {row.original.templateName}
                            </Text>
                            <Text
                              textStyle="bodyFamilyRegular"
                              fontSize="12px"
                              color="#4C4C4C"
                            >
                              {row.original.passType}
                            </Text>
                          </VStack>
                        ) : cell.column.id === 'addedToWallet' ? (
                          <Text
                            textStyle="bodyFamilyRegular"
                            fontSize="14px"
                            color="#4C4C4C"
                          >
                            {row.original.addedToWallet}
                          </Text>
                        ) : cell.column.id === 'status' ? (
                          <PassesRenderStatus
                            value={
                              row.original.attributes.expiresAt &&
                              new Date(row.original.attributes.expiresAt) <
                                new Date()
                                ? 'expired'
                                : row.original.attributes.status
                            }
                            hasTooltip
                          />
                        ) : cell.value !== null ? (
                          <Text>{cell.value}</Text>
                        ) : (
                          <Text as="span" color="secondaryDark06">
                            n/d
                          </Text>
                        );

                      return (
                        <Td
                          {...cell.getCellProps([
                            {
                              className: cell.column.className,
                              style: cell.column.style,
                            },
                            getColumnProps(cell.column),
                            getCellProps(cell),
                          ])}
                          isNumeric={cell.column.isNumeric}
                          py={isActions ? '12px' : '0'}
                          px={isActions ? '16px' : '0'}
                          verticalAlign={isActions ? 'middle' : undefined}
                          textAlign={isActions ? 'center' : undefined}
                        >
                          {isActions ? (
                            <Flex justify="center">
                              <Menu placement="bottom-end">
                                <MenuButton
                                  as={IconButton}
                                  aria-label="Actions"
                                  icon={
                                    <Text
                                      fontSize="12px"
                                      letterSpacing="2px"
                                      color="#989898"
                                      lineHeight="1"
                                    >
                                      •••
                                    </Text>
                                  }
                                  variant="unstyled"
                                  size="sm"
                                  _hover={{ '& p': { color: '#4C4C4C' } }}
                                  _active={{
                                    bg: 'transparent',
                                    '& p': { color: '#333' },
                                  }}
                                />
                                <MenuList
                                  minW="180px"
                                  boxShadow="md"
                                  borderRadius="8px"
                                  py={0}
                                  overflow="hidden"
                                >
                                  <MenuItem
                                    as={RouterLink}
                                    to={`/passes/${row.original.id}`}
                                    onClick={preserveListState}
                                    fontSize="14px"
                                    py="10px"
                                    px="16px"
                                    bg="white"
                                    _hover={{
                                      bg: '#F8F8F8',
                                      textDecoration: 'none',
                                    }}
                                    _focus={{
                                      bg: 'white',
                                      _hover: { bg: '#F8F8F8' },
                                    }}
                                    _active={{ bg: '#F8F8F8' }}
                                  >
                                    <CustomViewIcon boxSize="16px" mr="8px" />
                                    View details
                                  </MenuItem>
                                  {row.original.attributes.status !==
                                    'deactivated' &&
                                    row.original.attributes.status !==
                                      'expired' && (
                                      <MenuItem
                                        as={isReadOnly ? undefined : RouterLink}
                                        to={
                                          isReadOnly
                                            ? undefined
                                            : `/passes/edit/${row.original.id}`
                                        }
                                        onClick={
                                          isReadOnly
                                            ? undefined
                                            : preserveListState
                                        }
                                        isDisabled={isReadOnly}
                                        fontSize="14px"
                                        py="10px"
                                        px="16px"
                                        bg="white"
                                        _hover={{
                                          bg: '#F8F8F8',
                                          textDecoration: 'none',
                                        }}
                                        _focus={{
                                          bg: 'white',
                                          _hover: { bg: '#F8F8F8' },
                                        }}
                                        _active={{ bg: '#F8F8F8' }}
                                      >
                                        <CustomEditIcon
                                          boxSize="16px"
                                          mr="8px"
                                        />
                                        Edit pass
                                      </MenuItem>
                                    )}
                                  <MenuItem
                                    fontSize="14px"
                                    py="10px"
                                    px="16px"
                                    bg="white"
                                    _hover={{ bg: '#F8F8F8' }}
                                    _focus={{
                                      bg: 'white',
                                      _hover: { bg: '#F8F8F8' },
                                    }}
                                    _active={{ bg: '#F8F8F8' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const url =
                                        row.original.attributes.downloadUrl;
                                      if (url) {
                                        navigator.clipboard.writeText(url);
                                        toast({
                                          render: (props) => (
                                            <CustomToast
                                              status="success"
                                              title="Pass link copied"
                                              description=""
                                              onClose={props.onClose}
                                              position="top"
                                              duration={2000}
                                              id={props.id}
                                            />
                                          ),
                                        });
                                      }
                                    }}
                                  >
                                    <CustomCopyLinkIcon
                                      boxSize="16px"
                                      mr="8px"
                                    />
                                    Copy pass link
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Flex>
                          ) : (
                            <RouterLink
                              to={`/passes/${row.original.id}`}
                              onClick={preserveListState}
                              style={{
                                display: 'block',
                                padding: '12px 20px',
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              {cellContent}
                            </RouterLink>
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>

        {data.length > 0 && (
          <Flex
            mt={6}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={4}
          >
            <PassesListDownload />
            <ReactTablePagination
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
              pageCount={pageCount}
              gotoPage={gotoPage}
              nextPage={nextPage}
              previousPage={previousPage}
              setPageSize={setPageSize}
              pageIndex={pageIndex}
              pageSize={pageSize}
            />
          </Flex>
        )}
      </TableBox>
    </>
  );
}
